import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/store/hooks";

// Assets & Helpers
import { ArrowLeft } from "src/assets/svgs";
import { useOutsideClick } from "src/helpers";

// Actions
import { toggleSidebar } from "src/store/context/reducer";

// Components
import SearchUsers from "./search-users";
import CallHistory from "./call-history";
import FloatDialer from "./float-dialer";
import { createDialerInstance, DialerInstance } from "src/services/dialer";

// Types
enum SideStatus {
  OPENING,
  OPENNED,
  CLOSING,
  CLOSED,
}
type SidebarState = {
  sideStatus: SideStatus;
  sideType: string | null;
  bgOpacity: string;
  translateX: string;
  dialerInstance: DialerInstance | null;
};

export default function Aside() {
  // Sidebar type
  const dispatch = useAppDispatch();
  const { sidebar } = useAppSelector((state) => state.context);
  const dialerAudioElm = useRef<HTMLAudioElement | null>(null);

  // Inner state
  const [state, setState] = useState<SidebarState>({
    sideStatus: SideStatus.CLOSED,
    sideType: null,
    dialerInstance: null,

    // CSS
    bgOpacity: "bg-black/0",
    translateX: "translate-x-full",
  });

  let visibilityInterval: any;
  let step = 0;

  async function mountDialer() {
    const dialer = await createDialerInstance({ remoteAudioEl: dialerAudioElm.current || undefined }, "VOIP_HOME", { aderenteSlug: "icom-qa" });

    return dialer;
  }

  // Open flux
  useEffect(() => {
    async function toggle() {
      // Open
      if (sidebar && [SideStatus.CLOSED, SideStatus.CLOSING].includes(state.sideStatus)) {
        let subState: any = {};

        if (sidebar == "outsideDialer") {
          const dialerInstance = await mountDialer();
          subState.dialerInstance = dialerInstance;
        }

        setState((s) => ({
          ...s,
          ...subState,
          sideType: sidebar,
          sideStatus: SideStatus.OPENING,
        }));

        visibilityInterval = setInterval(() => {
          switch (step) {
            case 0:
              step++;

              setState((s) => ({ ...s, bgOpacity: "bg-black/70" }));
              break;

            case 1:
              step++;

              setState((s) => ({ ...s, translateX: "" }));
              break;

            default:
              step = 0;
              clearInterval(visibilityInterval);
              break;
          }
        }, 200);
      }

      // Closing
      else if (!sidebar && [SideStatus.OPENNED, SideStatus.OPENING].includes(state.sideStatus)) {
        let subState: any = {};

        // Force hangup
        if (state.dialerInstance) {
          state.dialerInstance?.destroy();

          subState.dialerInstance = null;
        }

        setState((s) => ({
          ...s,
          ...subState,
          translateX: "translate-x-full",
        }));

        visibilityInterval = setInterval(() => {
          switch (step) {
            case 0:
              step++;

              setState((s) => ({
                ...s,
                sideStatus: SideStatus.CLOSING,
              }));
              break;

            case 1:
              step++;

              setState((s) => ({
                ...s,
                bgOpacity: "bg-black/0",
                sideType: null,
              }));
              break;

            case 2:
              step++;

              setState((s) => ({
                ...s,
                sideStatus: SideStatus.CLOSED,
              }));
              break;

            default:
              step = 0;
              clearInterval(visibilityInterval);
              break;
          }
        }, 200);
      }
    }

    toggle();
  }, [sidebar]);

  useEffect(() => {
    // Save Dialer Output
    dialerAudioElm.current = document.getElementById("remote-dialer-audio") as HTMLAudioElement;
  }, []);

  const _toggleSidebar = () => dispatch(toggleSidebar(null));

  const sideRef = useRef(null);
  useOutsideClick(sideRef, _toggleSidebar);

  return state.sideStatus === SideStatus.CLOSED ? null : (
    <div className={`aside ${state.bgOpacity} absolute top-0 left-0 z-10 w-full h-full overflow-hidden transition-all`}>
      <aside ref={sideRef} className={`${state.translateX} absolute right-0 top-0 w-[466px] h-full transition-all p-8`}>
        <div className={`flex flex-col bg-white ${state.sideType == "outsideDialer" ? "" : "dark:bg-neutral-gray-800"} p-8 w-full h-full rounded-lg`}>
          <button
            type="button"
            className={`${state.sideType == "outsideDialer" ? "" : "dark:text-white"} mb-6 flex items-center text-base font-bold  text-primary-blue-500`}
            onClick={_toggleSidebar}
          >
            <ArrowLeft className={`${state.sideType == "outsideDialer" ? "" : "dark:stroke-white"} stroke-primary-blue-500 mr-1`} /> Voltar
          </button>

          {state.sideType == "searchUsers" && <SearchUsers />}
          {state.sideType == "callHistory" && <CallHistory />}
          {state.sideType == "outsideDialer" && state.dialerInstance && <FloatDialer dialer={state.dialerInstance} />}
        </div>
      </aside>
    </div>
  );
}
